"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowLeft, Loader2 } from "lucide-react"
import { motion } from "framer-motion"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { Suspense, useState } from "react"
import { FcGoogle } from "react-icons/fc"
import { SubmitHandler, useForm } from "react-hook-form"
import { Label } from "../../../components/landing/components/Fields"
import { Alert } from "../../../components/ui/Alert"
import { Button } from "../../../components/ui/button"
import { Logo } from "../../../components/ui/Logo"
import { AuthLayout } from "../../../layouts/AuthLayout"
import useAuth from "../../../hooks/useAuth"
import { signInSchema } from "../../../validation/auth"

interface SignInFormValues {
  email: string
  password: string
}

export default function SignIn() {
  return (
    <Suspense>
      <SignInForm />
    </Suspense>
  )
}

const inputClassName =
  "block w-full rounded-xl border border-gray-700 bg-gray-950/60 px-4 py-3 text-sm text-gray-100 placeholder-gray-500 shadow-inner transition-colors focus:border-emerald-500/80 focus:bg-gray-950 focus:outline-none focus:ring-2 focus:ring-emerald-500/25"

function SignInForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const urlError = searchParams.get("error")
  const nextParam = searchParams.get("next")
  const safeNext = nextParam && nextParam.startsWith("/") ? nextParam : null
  const [loading, setLoading] = useState(false)
  const { signInWithEmail, signInWithGoogle } = useAuth()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError
  } = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema)
  })

  const onSubmit: SubmitHandler<SignInFormValues> = async (values) => {
    setLoading(true)
    try {
      const { data, error } = await signInWithEmail(values.email, values.password)
      if (error) {
        setError("root", { type: "manual", message: error.message || "Invalid email or password" })
      } else if (data.user) {
        router.push(safeNext ?? "/onboarding")
      }
    } catch (error) {
      console.error("Sign-in error:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    try {
      const { error } = await signInWithGoogle()
      if (error) {
        setError("root", { type: "manual", message: error.message })
        setLoading(false)
      }
    } catch (error) {
      console.error("Google sign-in error:", error)
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="space-y-8"
      >
        <div>
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-gray-200"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            {"Back to home"}
          </Link>
          <div className="mt-8 flex justify-center">
            <Link
              href="/"
              className="inline-flex w-full justify-center transition-opacity hover:opacity-90"
              aria-label="Seedmancer home"
            >
              <Logo variant="wordmark" />
            </Link>
          </div>
          <h1 className="mt-8 text-center text-2xl font-semibold tracking-tight text-gray-50">
            {"Welcome back"}
          </h1>
          <p className="mt-2 text-center text-sm text-gray-400">
            {"Don’t have an account?"}{" "}
            <Link
              href="/auth/sign-up"
              className="font-medium text-emerald-400 transition-colors hover:text-emerald-300"
            >
              {"Sign up"}
            </Link>
          </p>
        </div>

        <div className="space-y-6">
          <Button
            type="button"
            variant="outline"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border-gray-700 bg-white/[0.04] py-0 text-[15px] font-medium transition-colors hover:bg-white/[0.08]"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            ) : (
              <FcGoogle className="h-5 w-5" aria-hidden />
            )}
            <span>{"Continue with Google"}</span>
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden>
              <div className="w-full border-t border-gray-700" />
            </div>
            <div className="relative flex justify-center text-xs font-medium uppercase tracking-wide">
              <span className="bg-gray-900 px-3 text-gray-400">{"or email"}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {(errors.root || urlError) && (
              <Alert
                title={
                  errors.root?.message ??
                  (urlError === "oauth_failed"
                    ? "Google sign-in failed. Please try again."
                    : "An error occurred. Please try again.")
                }
              />
            )}

            <div className="space-y-1.5">
              <Label id="email">{"Email"}</Label>
              <input
                id="email"
                className={inputClassName}
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label id="password">{"Password"}</Label>
              <input
                id="password"
                className={inputClassName}
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                {...register("password")}
              />
              {errors.password && (
                <p className="text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>

            <div className="space-y-4 pt-1">
              <Button
                type="submit"
                variant="filled"
                className="h-12 w-full rounded-xl text-[15px] font-semibold"
                disabled={isSubmitting || loading}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                ) : (
                  "Sign in"
                )}
              </Button>

              <Link
                href="/auth/reset-password"
                className="block text-center text-sm text-emerald-400/90 transition-colors hover:text-emerald-300"
              >
                {"Forgot password?"}
              </Link>
            </div>
          </form>
        </div>
      </motion.div>
    </AuthLayout>
  )
}
